// Validation Service - Main entry point for all validation operations
import type { FormField } from '../../types/form-builder.types';
import type { FormValidationError } from '../../types/form-submission.types';
import type { ValidationContext } from './types';
import { FieldValidatorFactory } from './field-validator-factory';

/**
 * Validation Service - provides high-level validation API
 * Implements Facade Pattern to simplify validation operations
 */
export class ValidationService {
  /**
   * Validate a single field
   */
  static async validateField(
    field: FormField,
    value: any,
    formData: Record<string, any>
  ): Promise<FormValidationError | null> {
    const validator = FieldValidatorFactory.createValidatorForField(field);

    const context: ValidationContext = {
      fieldId: field.id,
      fieldName: field.config.label,
      value,
      formData,
      config: field.config
    };

    const result = await validator.validate(context);

    if (!result.isValid) {
      return {
        fieldId: field.id,
        fieldName: field.config.label,
        message: result.message || 'Validation failed'
      };
    }

    return null;
  }

  /**
   * Validate multiple fields
   */
  static async validateFields(
    fields: FormField[],
    formData: Record<string, any>
  ): Promise<Record<string, FormValidationError>> {
    const errors: Record<string, FormValidationError> = {};

    for (const field of fields) {
      // Skip payment fields - they handle their own validation
      if (field.type === 'payment') {
        continue;
      }

      // Skip reCAPTCHA Enterprise fields - token is generated automatically before submission
      if (field.type === 'captcha' && (field.config as any)?.captchaType === 'recaptcha-enterprise') {
        continue;
      }

      const value = formData[field.id];
      const error = await this.validateField(field, value, formData);

      if (error) {
        errors[field.id] = error;
      }
    }

    return errors;
  }

  /**
   * Validate only required fields (useful for page validation)
   */
  static async validateRequiredFields(
    fields: FormField[],
    formData: Record<string, any>
  ): Promise<Record<string, FormValidationError>> {
    const requiredFields = fields.filter(field => field.config.required);
    return this.validateFields(requiredFields, formData);
  }

  /**
   * Check if a value passes validation for a field
   */
  static async isValid(
    field: FormField,
    value: any,
    formData: Record<string, any>
  ): Promise<boolean> {
    const error = await this.validateField(field, value, formData);
    return error === null;
  }

  /**
   * Validate a single field by ID (convenience method)
   */
  static async validateFieldById(
    fieldId: string,
    allFields: FormField[],
    formData: Record<string, any>
  ): Promise<FormValidationError | null> {
    const field = allFields.find(f => f.id === fieldId);

    if (!field) {
      console.warn(`Field ${fieldId} not found for validation`);
      return null;
    }

    const value = formData[fieldId];
    return this.validateField(field, value, formData);
  }
}

